import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import PaymentApprovalModal from "./PaymentApprovalModal"; // Ní nhớ tạo Modal tương ứng nhé

const PaymentApprovalConfigList = () => {
  const [configs, setConfigs] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  // Lấy toàn bộ cấu hình duyệt chi tiền từ API
  const fetchConfigs = async () => {
    try {
      console.log("Calling API:", API_ENDPOINTS.paymentApprovalConfigs);
      const res = await axiosClient.get(API_ENDPOINTS.paymentApprovalConfigs);
      console.log("Payment configs received:", res.data);
      setConfigs(res.data || []);
    } catch (error) {
      console.error("❌ Error loading payment configurations:", error);
    }
  };

  // Xóa một bước cấu hình duyệt
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this payment approval step?")) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.paymentApprovalConfigs}/${id}`);
      fetchConfigs();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Delete failed! Please try again.");
    }
  };

  // Lưu cấu hình (Thêm mới hoặc Cập nhật)
  const handleSave = async (data) => {
    try {
      // Vì chạy 1 luồng tập trung nên gán cứng quy trình ConfigGroup là "General"
      const payload = {
        ...data,
        configGroup: "General"
      };

      if (editingConfig && editingConfig.id) {
        await axiosClient.put(`${API_ENDPOINTS.paymentApprovalConfigs}/${editingConfig.id}`, payload);
      } else {
        await axiosClient.post(API_ENDPOINTS.paymentApprovalConfigs, payload);
      }
      fetchConfigs();
      setShowModal(false);
      setEditingConfig(null);
    } catch (error) {
      console.error("Save error:", error);
      alert("Error saving payment configuration! Please check data details.");
    }
  };

  // Sắp xếp thứ tự các bước duyệt từ nhỏ đến lớn
  const sortedData = [...configs].sort((a, b) => a.stepOrder - b.stepOrder);

  const thStyle = { 
    backgroundColor: "#f8f9fc", 
    color: "#4e73df", 
    fontWeight: "bold", 
    fontSize: "0.8rem", 
    textTransform: "uppercase",
    letterSpacing: "0.5px"
  };

  // Hàm helper để map giá trị Enum Chức vụ (UserPosition) sang text hiển thị thân thiện
  const renderPosition = (pos) => {
    if (pos === 0 || pos === "Staff") return "Staff";
    if (pos === 1 || pos === "Manager") return "Manager";
    if (pos === 2 || pos === "Director") return "Director";
    return pos || "N/A";
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h5 className="m-0 font-weight-bold text-dark">PAYMENT REQUEST APPROVAL WORKFLOW</h5>
              <p className="text-muted small mb-0">Configure the sequential chain of authorities required for finance and payout approvals</p>
            </div>
            <button
              className="btn btn-sm btn-primary px-3 rounded-pill shadow-sm fw-bold"
              onClick={() => {
                setEditingConfig({ 
                  configGroup: "General", 
                  stepOrder: sortedData.length + 1,
                  isCCOnly: false,
                  position: "Staff"
                });
                setShowModal(true);
              }}
            >
              <i className="fas fa-plus me-1"></i> Add Approval Step
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={thStyle}>Sequence</th>
                  <th style={thStyle}>Approver Information</th>
                  <th style={thStyle}>Designated Position</th>
                  <th style={thStyle}>Purpose / Notes</th>
                  <th className="text-center" style={thStyle}>Step Type</th>
                  <th className="text-center" style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedData.length > 0 ? (
                  sortedData.map((c) => (
                    <tr key={c.id}>
                      {/* Số thứ tự bước duyệt */}
                      <td className="text-center">
                        <span className="badge rounded-circle bg-primary text-white" style={{ width: "28px", height: "28px", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                          {c.stepOrder}
                        </span>
                      </td>

                      {/* Thông tin người duyệt đích danh */}
                      <td>
                        <div className="fw-bold text-dark">
                          {c.approver?.fullName || (c.approverId ? `User ID: ${c.approverId}` : "Flexible (By Position)")}
                        </div>
                        {c.approver?.email && (
                          <div className="small text-muted">
                            <i className="far fa-envelope me-1"></i>
                            {c.approver.email}
                          </div>
                        )}
                      </td>

                      {/* Chức vụ đảm nhiệm */}
                      <td>
                        <span className="badge bg-secondary text-white px-2 py-1">
                          {renderPosition(c.position)}
                        </span>
                      </td>

                      {/* Ghi chú nội dung quy trình */}
                      <td>
                        <div className="text-dark small" style={{ maxWidth: "300px" }}>
                          {c.note || <span className="text-muted fst-italic">No specific instructions</span>}
                        </div>
                      </td>

                      {/* Phân loại loại bước: Duyệt ký tên hay nhận bản sao CC */}
                      <td className="text-center">
                        {c.isCCOnly ? (
                          <span className="badge bg-light text-warning border border-warning px-3">
                            <i className="fas fa-info-circle me-1"></i> CC Only
                          </span>
                        ) : (
                          <span className="badge bg-light text-success border border-success px-3">
                            <i className="fas fa-check-circle me-1"></i> Authorized Signer
                          </span>
                        )}
                      </td>

                      {/* Các nút thao tác Sửa / Xóa */}
                      <td className="text-center">
                        <div className="d-flex justify-content-center gap-2">
                          <button
                            className="btn btn-sm btn-light text-primary border rounded-circle shadow-sm"
                            title="Modify Step"
                            onClick={() => {
                              setEditingConfig(c);
                              setShowModal(true);
                            }}
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="btn btn-sm btn-light text-danger border rounded-circle shadow-sm"
                            title="Remove Step"
                            onClick={() => handleDelete(c.id)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="text-muted">
                        <i className="fas fa-wallet fa-3x mb-3 opacity-25"></i>
                        <p className="mb-0">No approval configuration stages found for the payment workflow.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white border-0 py-3">
          <small className="text-muted fst-italic">
            * Workflow logic: Payment requests will route through each sequence number sequentially. Steps designated as "CC Only" receive notifications but do not halt the approval progression.
          </small>
        </div>
      </div>

      {/* Gọi Modal để thêm mới / chỉnh sửa thông tin */}
      {showModal && (
        <PaymentApprovalModal
          show={showModal}
          initialData={editingConfig}
          onClose={() => {
            setShowModal(false);
            setEditingConfig(null);
          }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default PaymentApprovalConfigList;